/**
 * Color Role - Format Pillar
 *
 * Formatação de cores para apresentação.
 *
 * @example
 * import { formatColor } from '@attrx/role-morphic/color/format';
 *
 * formatColor('hex', '#ff0000', { uppercase: true }); // '#FF0000'
 * formatColor('rgb_string', { r: 255, g: 0, b: 0 });  // 'rgb(255, 0, 0)'
 */

import type { ColorVariant, RGBA, ColorRgbObject, ColorHslObject, ColorFormatOptions } from "./types";
import { rgbToHsl } from "./types";
import { toBaseColor } from "./convert";

// =============================================================================
// FORMAT OPTIONS DEFAULTS
// =============================================================================

const DEFAULT_OPTIONS: ColorFormatOptions = {
  uppercase: false,
  includeAlpha: false,
  compact: false,
};

// =============================================================================
// FORMAT BY VARIANT
// =============================================================================

/**
 * Formata RGBA como hex
 */
function formatAsHex(rgba: RGBA, options: ColorFormatOptions): string {
  const r = rgba.r.toString(16).padStart(2, "0");
  const g = rgba.g.toString(16).padStart(2, "0");
  const b = rgba.b.toString(16).padStart(2, "0");

  let hex: string;

  if (options.includeAlpha || rgba.a < 1) {
    const a = Math.round(rgba.a * 255)
      .toString(16)
      .padStart(2, "0");
    hex = `#${r}${g}${b}${a}`;
  } else {
    hex = `#${r}${g}${b}`;
  }

  // Compact: try to use shorthand if possible
  if (options.compact && !options.includeAlpha && rgba.a === 1) {
    if (r[0] === r[1] && g[0] === g[1] && b[0] === b[1]) {
      hex = `#${r[0]}${g[0]}${b[0]}`;
    }
  }

  return options.uppercase ? hex.toUpperCase() : hex.toLowerCase();
}

/**
 * Formata RGBA como RGB string
 */
function formatAsRgbString(rgba: RGBA, options: ColorFormatOptions): string {
  if (options.includeAlpha || rgba.a < 1) {
    if (options.compact) {
      return `rgba(${rgba.r},${rgba.g},${rgba.b},${rgba.a})`;
    }
    return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
  }

  if (options.compact) {
    return `rgb(${rgba.r},${rgba.g},${rgba.b})`;
  }
  return `rgb(${rgba.r}, ${rgba.g}, ${rgba.b})`;
}

/**
 * Formata RGBA como HSL string
 */
function formatAsHslString(rgba: RGBA, options: ColorFormatOptions): string {
  const { h, s, l } = rgbToHsl(rgba.r, rgba.g, rgba.b);

  if (options.includeAlpha || rgba.a < 1) {
    if (options.compact) {
      return `hsla(${h},${s}%,${l}%,${rgba.a})`;
    }
    return `hsla(${h}, ${s}%, ${l}%, ${rgba.a})`;
  }

  if (options.compact) {
    return `hsl(${h},${s}%,${l}%)`;
  }
  return `hsl(${h}, ${s}%, ${l}%)`;
}

/**
 * Formata RGBA como RGB object (formato CSS rgb())
 * Nota: rgb_object format usa a mesma representação de rgb_string
 */
function formatAsRgbObject(rgba: RGBA, options: ColorFormatOptions): string {
  // rgb_object format outputs RGB string representation (same as rgb_string)
  return formatAsRgbString(rgba, options);
}

/**
 * Formata RGBA como HSL object (formato CSS hsl())
 * Nota: hsl_object format usa a mesma representação de hsl_string
 */
function formatAsHslObject(rgba: RGBA, options: ColorFormatOptions): string {
  // hsl_object format outputs HSL string representation (same as hsl_string)
  return formatAsHslString(rgba, options);
}

// =============================================================================
// MAIN FORMAT
// =============================================================================

/**
 * Formata cor para apresentação
 *
 * @param variant - Variante de saída
 * @param value - Valor da cor (qualquer variante)
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatColor('hex', '#ff0000', { uppercase: true });
 * // '#FF0000'
 *
 * formatColor('rgb_string', { r: 255, g: 0, b: 0 });
 * // 'rgb(255, 0, 0)'
 *
 * formatColor('hex', '#ff0000', { compact: true });
 * // '#f00'
 */
export function formatColor(
  variant: ColorVariant,
  value: unknown,
  options?: ColorFormatOptions,
): string {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // Primeiro converte para RGBA (base)
  let rgba: RGBA;

  // Se o value já é um objeto com r,g,b,a, usa direto
  if (
    typeof value === "object" &&
    value !== null &&
    "r" in value &&
    "g" in value &&
    "b" in value
  ) {
    const obj = value as ColorRgbObject;
    rgba = {
      r: obj.r,
      g: obj.g,
      b: obj.b,
      a: obj.a ?? 1,
    };
  } else if (
    typeof value === "object" &&
    value !== null &&
    "h" in value &&
    "s" in value &&
    "l" in value
  ) {
    // HSL object - converte para RGBA
    rgba = toBaseColor("hsl_object", value);
  } else if (typeof value === "string") {
    // String - detecta formato e converte
    rgba = toBaseColor(detectStringVariant(value), value);
  } else {
    throw new Error(`Cannot format color: ${String(value)}`);
  }

  // Formata na variante de saída
  switch (variant) {
    case "hex":
      return formatAsHex(rgba, opts);
    case "rgb_string":
      return formatAsRgbString(rgba, opts);
    case "hsl_string":
      return formatAsHslString(rgba, opts);
    case "rgb_object":
      return formatAsRgbObject(rgba, opts);
    case "hsl_object":
      return formatAsHslObject(rgba, opts);
    default:
      throw new Error(`Unknown color variant: ${variant}`);
  }
}

/**
 * Detecta variante de uma string
 */
function detectStringVariant(value: string): ColorVariant {
  const trimmed = value.trim().toLowerCase();

  if (/^#?[0-9a-f]{3,8}$/i.test(trimmed)) {
    return "hex";
  }
  if (/^rgba?\s*\(/i.test(trimmed)) {
    return "rgb_string";
  }
  if (/^hsla?\s*\(/i.test(trimmed)) {
    return "hsl_string";
  }

  // Default to hex
  return "hex";
}

// =============================================================================
// CONVENIENCE FUNCTIONS
// =============================================================================

/**
 * Formata como hex
 */
export function formatHex(value: unknown, options?: ColorFormatOptions): string {
  return formatColor("hex", value, options);
}

/**
 * Formata como RGB string
 */
export function formatRgbString(value: unknown, options?: ColorFormatOptions): string {
  return formatColor("rgb_string", value, options);
}

/**
 * Formata como HSL string
 */
export function formatHslString(value: unknown, options?: ColorFormatOptions): string {
  return formatColor("hsl_string", value, options);
}
